import { useCallback, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import axios from "axios"
import Swal from "sweetalert2"
import { toast } from "react-toastify"
import { baseUrl } from "../../config/baseUrl"
import PermissionGuard from "../PermissionGuard/PermissionGuard"
import { PERMISSIONS } from "../../utils/permissions"

export default function FaqList() {
    const [faqs, setFaqs] = useState([])
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")
    const token = localStorage.getItem("token")

    const fetchFaqs = useCallback(async () => {
        setLoading(true)
        try {
            const response = await axios.get(`${baseUrl}/faq`, {
                headers: {
                    Authorization: token,
                },
            })
            setFaqs(Array.isArray(response?.data?.data) ? response.data.data : [])
        } catch (error) {
            console.error("Error fetching FAQs:", error)
            toast.error("Failed to fetch FAQs")
        } finally {
            setLoading(false)
        }
    }, [token])

    useEffect(() => {
        fetchFaqs()
    }, [fetchFaqs])

    const filteredFaqs = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase()
        if (!normalizedSearch) {
            return faqs
        }

        return faqs.filter((faq) =>
            [
                faq.question,
                faq.answer,
                faq.questionHindi,
                faq.answerHindi,
                String(faq.sortOrder ?? ""),
                faq.status,
            ]
                .filter(Boolean)
                .some((value) => value.toLowerCase().includes(normalizedSearch))
        )
    }, [faqs, searchTerm])

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "Delete FAQ?",
            text: "This FAQ will be removed permanently.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it",
        })

        if (!result.isConfirmed) {
            return
        }

        try {
            await axios.delete(`${baseUrl}/faq/${id}`, {
                headers: {
                    Authorization: token,
                },
            })
            setFaqs((prev) => prev.filter((faq) => faq._id !== id))
            toast.success("FAQ deleted successfully")
        } catch (error) {
            console.error("Error deleting FAQ:", error)
            toast.error("Failed to delete FAQ")
        }
    }

    const handleStatusToggle = async (id, currentStatus) => {
        const newStatus = currentStatus === "active" ? "inactive" : "active"

        try {
            await axios.patch(
                `${baseUrl}/faq/${id}/status`,
                { status: newStatus },
                {
                    headers: {
                        Authorization: token,
                    },
                }
            )
            setFaqs((prev) =>
                prev.map((faq) =>
                    faq._id === id ? { ...faq, status: newStatus } : faq
                )
            )
            toast.success("FAQ status updated successfully")
        } catch (error) {
            console.error("Error updating FAQ status:", error)
            toast.error("Failed to update FAQ status")
        }
    }

    const renderText = (value) => {
        if (!value) {
            return "-"
        }

        return value.length > 90 ? `${value.slice(0, 90)}...` : value
    }

    return (
        <section className="main-sec">
            <div className="row">
                <div className="col-lg-6">
                    <div className="dashboard-title">
                        <h4 className="dash-head">
                            <i className="fa fa-question-circle me-2" />
                            FAQ List
                        </h4>
                    </div>
                    <div className="custom-bredcump">
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to="/">Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item active" aria-current="page">
                                    FAQs
                                </li>
                            </ol>
                        </nav>
                    </div>
                </div>
                <div className="col-lg-6 text-end">
                    <PermissionGuard permission={PERMISSIONS.FAQ_CREATE}>
                        <Link to="/faqs/add" className="btn py-2 px-5 text-white btn-add-question">
                            <i className="fa fa-plus me-2"></i>
                            Add FAQ
                        </Link>
                    </PermissionGuard>
                </div>

                <div className="col-lg-12">
                    <div className="cards bus-list">
                        <div className="bus-filter">
                            <div className="row align-items-center">
                                <div className="col-lg-6">
                                    <h5 className="card-title mb-0">FAQ List</h5>
                                </div>
                                <div className="col-lg-6">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search question, answer, Hindi text, status..."
                                        value={searchTerm}
                                        onChange={(event) => setSearchTerm(event.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="table table-responsive custom-table">
                            <table className="table table-borderless">
                                <thead>
                                    <tr>
                                        <th>SN</th>
                                        <th>Question</th>
                                        <th>Hindi Question</th>
                                        <th>Sort Order</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={6} className="text-center py-4">
                                                Loading FAQs...
                                            </td>
                                        </tr>
                                    ) : filteredFaqs.length > 0 ? (
                                        filteredFaqs.map((faq, index) => (
                                            <tr key={faq._id}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <div className="fw-semibold">{renderText(faq.question)}</div>
                                                    <small className="text-muted d-block mt-1">
                                                        {renderText(faq.answer)}
                                                    </small>
                                                </td>
                                                <td>
                                                    <div className="fw-semibold">{renderText(faq.questionHindi)}</div>
                                                    <small className="text-muted d-block mt-1">
                                                        {renderText(faq.answerHindi)}
                                                    </small>
                                                </td>
                                                <td>{faq.sortOrder ?? 0}</td>
                                                <td>
                                                    <PermissionGuard
                                                        permission={PERMISSIONS.FAQ_EDIT}
                                                        fallback={
                                                            <span
                                                                className={
                                                                    faq.status === "active"
                                                                        ? "badge bg-success"
                                                                        : "badge bg-danger"
                                                                }
                                                            >
                                                                {faq.status}
                                                            </span>
                                                        }
                                                    >
                                                        <button
                                                            type="button"
                                                            className={
                                                                faq.status === "active"
                                                                    ? "btn btn-pill btn-primary btn-sm"
                                                                    : "btn btn-pill btn-danger btn-sm"
                                                            }
                                                            onClick={() => handleStatusToggle(faq._id, faq.status)}
                                                        >
                                                            {faq.status}
                                                        </button>
                                                    </PermissionGuard>
                                                </td>
                                                <td>
                                                    <div className="action-buttons">
                                                        <PermissionGuard permission={PERMISSIONS.FAQ_EDIT}>
                                                            <Link
                                                                className="action-btn edit-btn"
                                                                to={`/faqs/edit/${faq._id}`}
                                                                title="Edit"
                                                            >
                                                                <i className="fa fa-edit" />
                                                            </Link>
                                                        </PermissionGuard>
                                                        <PermissionGuard permission={PERMISSIONS.FAQ_DELETE}>
                                                            <button
                                                                type="button"
                                                                className="action-btn delete-btn"
                                                                title="Delete"
                                                                onClick={() => handleDelete(faq._id)}
                                                            >
                                                                <i className="fa fa-trash"></i>
                                                                <span className="tooltip-text">Delete</span>
                                                            </button>
                                                        </PermissionGuard>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={6} className="text-center py-4">
                                                {searchTerm.trim() ? "No matching FAQs found." : "No FAQs added yet."}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
